import { useEffect, useRef, useState } from 'react';
import { IoMdArrowDropdown } from 'react-icons/io';

type Option = {
  label: string;
  value: string;
};

interface MultiSelectDropdownProps {
  hintText: string;
  options: Option[];
  selected: string[];
  onChange: (selected: string[]) => void;
}

const MultiSelectDropdown = (props: MultiSelectDropdownProps) => {
  const { hintText, options, selected, onChange } = props;
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const clearSelections = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    onChange([]);
  };

  const toggleOption = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative z-50 w-full">
      <div className="relative w-full" ref={dropdownRef}>
        <label
          tabIndex={0}
          className="btn btn-outline btn-sm border-base-300 text-sm font-normal overflow-clip whitespace-nowrap w-full justify-start"
          onClick={() => setIsOpen(!isOpen)}>
          <span className="flex-1 text-left">
            {selected.length > 0
              ? selected.length === 1
                ? options.find((item) => item.value === selected[0])?.label
                : `${selected.length} selected`
              : hintText}
          </span>
          <IoMdArrowDropdown
            className={`transition-transform duration-200 flex-shrink-0 ${isOpen ? 'rotate-180' : ''}`}
            size={16}
          />
        </label>
        {isOpen && (
          <ul className="absolute top-full left-0 mt-1 bg-base-100 border border-base-300 rounded-box w-full shadow-lg z-50 max-h-60 overflow-y-auto p-2">
            <li key={'CLEARALL'}>
              <label className="label cursor-pointer p-0">
                <button className="btn btn-link btn-sm" onClick={(e) => clearSelections(e)}>
                  Clear Selections
                </button>
              </label>
            </li>
            {options.map((option) => (
              <li key={option.value}>
                <label className="label cursor-pointer justify-start gap-2 px-2 py-1 hover:bg-base-200 rounded">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-sm"
                    checked={selected.includes(option.value)}
                    onChange={() => toggleOption(option.value)}
                  />
                  <span className="label-text">{option.label}</span>
                </label>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MultiSelectDropdown;
